import { useState, useEffect, useMemo } from 'react';
import { localToday } from '../utils/localDate';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from "../supabase";
import { logGlobalAction, formatDateDDMMYY } from "../constants";
import { calculateDailyStats, calculateVacationDays } from '../hr_logic/hrUtils';
import { sendPushNotification } from '../utils.tsx';
import { useAuth } from './useAuth';
import { queryKeys } from '../lib/queryKeys';
import { supabaseQuery } from '../lib/supabaseQuery';
import type { HRLog } from '../hr_logic/hrUtils';

/** hr_state table row (stempeluhr status per user/day). */
interface HRStateRow {
    status?: string | null;
    accumulated_ms?: number | null;
    session_start?: string | null;
    date?: string | null;
}

/** hr_requests table row. */
interface HRRequestRow {
    user_id?: string;
    start_date?: string | null;
    end_date?: string | null;
    days?: number | null;
    type?: string | null;
    status?: string | null;
    data?: { days?: number; userName?: string; date?: string; absenceManagers?: string[] } | null;
    [key: string]: unknown;
}

/** settings table row carrying the hr_settings JSONB config. */
interface HrSettingsRow {
    config?: Record<string, unknown> | null;
}

/**
 * App-level user shape built by useAppAuth (AppAuth.tsx) — the flattened
 * profile consumed across the app (NOT the raw Supabase Auth user).
 */
export interface AppUser {
    id: string;
    name: string;
    email?: string;
    role?: string;
    tenantId: string | null;
    holidayState?: string | null;
    workdays?: boolean[];
    targetHoursPerDay?: number;
    /** Property IDs the user prefers; empty until the profile loads. */
    preferredProperties?: string[];
    /** Fallback single property id (legacy field; pre-preferences era). */
    propertyId?: string;
    /** Custom sidebar menu groups persisted in users.menu_groups (JSONB). */
    menuGroups?: MenuGroup[];
    /** HR department identifier (e.g. 'HK', 'HT', 'FO'). */
    department?: string;
    /** Loyalty program points balance. */
    loyaltyPoints?: number;
    [key: string]: unknown;
}

/** One customizable sidebar menu group (users.menu_groups rows). */
export interface MenuGroup {
    name: string;
    items: string[];
    color: string;
}



/**
 * Hook for personal HR time tracking and requests.
 *
 * Manages stempeluhr (clock in/out) state, manual corrections, and absence requests.
 * Includes a live ticker for current session duration. This is the primary entry point
 * for users to manage their daily attendance.
 *
 * Migrated to TanStack Query (Phase 1):
 *   - 4 ad-hoc Supabase fetches → useQuery (hr_state, time_logs, hr_requests, settings)
 *   - 4 realtime channels → 1 combined channel that invalidates query keys
 *   - Mutations remain direct supabase calls (useMutation migration in Task 5+ pattern)
 *   - The realtime channel here is temporary — Phase 2's centralized realtimeStore
 *     will take over, at which point this useEffect is removed entirely.
 *
 * @param {Object} user - The currently authenticated user.
 * @returns {Object} An object containing:
 * @returns {string} .clockStatus - Current status ('in', 'out', 'break').
 * @returns {number} .accumulatedMs - Total milliseconds worked today (excluding current session).
 * @returns {number} .displayMs - Total milliseconds worked today (including live ticker).
 * @returns {Array} .allTimeLogs - History of clock events for the user.
 * @returns {Array} .requests - List of absence requests (vacation, etc.).
 * @returns {Object} .dailyStats - Calculated daily totals and averages.
 * @returns {boolean} .showMissingTimeModal - UI state for ghost-shift warning.
 * @returns {Function} .setShowMissingTimeModal - Setter for missing time modal visibility.
 * @returns {Object} .missingShiftData - Data about a shift that wasn't closed correctly.
 * @returns {Function} .handleClockAction - Main handler for in/out/break actions.
 * @returns {Function} .saveEditedTime - Manually adds or removes working time.
 * @returns {Function} .saveManualPause - Manually adds a pause duration.
 * @returns {Function} .submitRequest - Submits a new absence request.
 * @sideeffect Listens to user-specific Supabase hr_state, time_logs, and hr_requests tables.
 * @sideeffect Updates stempeluhr state and logs actions to Supabase.
 */
export function useHRData(user: AppUser | null) {
    const { t } = useTranslation();
    const { getToken } = useAuth();
    const queryClient = useQueryClient();
    const stableUserId = user?.id || 'unbekannt';
    const activeTenantId = user?.tenantId;
    const today = localToday();

    // ── UI-only state (not server state) ───────────────────────────
    const [displayMs, setDisplayMs] = useState(0);
    const [dismissedGhostDate, setDismissedGhostDate] = useState<string | null>(null);

    // ── Server state: hr_state ─────────────────────────────────────
    const { data: hrState } = useQuery<HRStateRow | null>({
        queryKey: queryKeys.hr.state(stableUserId, today),
        queryFn: supabaseQuery(db => db.from('hr_state')
            .select('*')
            .eq('user_id', stableUserId)
            .eq('date', today)
            .maybeSingle()),
        staleTime: 0, // always refetch on mount — critical for clock state accuracy
        enabled: !!user,
    });

    // Derived clock state from query data.
    // This replaces the old setClockStatus/setAccumulatedMs/setCurrentSessionStart
    // useState calls. The query IS the source of truth — no local copy.
    const clockStatus = hrState?.status || 'out';
    const accumulatedMs = hrState?.accumulated_ms || 0;
    const currentSessionStart = hrState?.session_start
        ? new Date(hrState.session_start).getTime() : null;

    // Ghost-shift detection: query returns a row from a previous day and
    // user was never clocked out. Derived during render (no setState in effect).
    // `dismissedGhostDate` lets the user dismiss the modal without it
    // reappearing on every refetch.
    const missingShiftData = (hrState && hrState.date && hrState.date !== today && hrState.status !== 'out')
        ? hrState : null;
    const showMissingTimeModal = missingShiftData && missingShiftData.date !== dismissedGhostDate;
    const setShowMissingTimeModal = (v: boolean) => {
        if (v === false && missingShiftData?.date) {
            setDismissedGhostDate(missingShiftData.date);
        }
    };
    const setMissingShiftData = () => { /* no-op: derived from query data */ };

    // ── Server state: time_logs ────────────────────────────────────
    const { data: allTimeLogs } = useQuery<HRLog[]>({
        queryKey: queryKeys.hr.timeLogs(stableUserId),
        queryFn: supabaseQuery(db => db.from('time_logs')
            .select('*')
            .eq('user_id', stableUserId)
            .order('timestamp', { ascending: false })
            .limit(1000)),
        enabled: !!user,
    });

    // ── Server state: hr_requests (normalized) ────────────────────
    const { data: rawRequests } = useQuery<HRRequestRow[]>({
        queryKey: queryKeys.hr.requests(stableUserId),
        queryFn: supabaseQuery(db => db.from('hr_requests')
            .select('*')
            .eq('user_id', stableUserId)
            .order('created_at', { ascending: false })
            .limit(50)),
        enabled: !!user,
    });

    const requests = (() => {
        if (!rawRequests) return [];
        return rawRequests.map(req => ({
            ...req,
            userId: req.user_id,
            startDate: req.start_date,
            endDate: req.end_date,
            days: req.days ?? req.data?.days ?? 0,
            userName: req.data?.userName ?? '',
            date: req.data?.date ?? ''
        }));
    })();

    // ── Server state: hr_settings ──────────────────────────────────
    const { data: hrSettingsData } = useQuery<HrSettingsRow | null>({
        queryKey: queryKeys.settings.byId(activeTenantId ?? '', 'hr_settings'),
        queryFn: supabaseQuery(db => db.from('settings')
            .select('*')
            .eq('tenant_id', activeTenantId)
            .eq('id', 'hr_settings')
            .maybeSingle()),
        enabled: !!activeTenantId,
    });
    const hrSettings = hrSettingsData?.config || {};

    // Realtime handled by centralized realtimeStore (src/lib/realtimeStore.js)

    // ── Live Ticker ────────────────────────────────────────────────
    useEffect(() => {
        const timer = setInterval(() => {
            if (clockStatus === 'in' && currentSessionStart) {
                setDisplayMs(accumulatedMs + (new Date().getTime() - currentSessionStart));
            } else {
                setDisplayMs(accumulatedMs);
            }
        }, 1000);
        return () => clearInterval(timer);
    }, [clockStatus, currentSessionStart, accumulatedMs]);

    // ── Actions ────────────────────────────────────────────────────

    /**
     * Handles primary clock events (in, out, break).
     * Calculates accumulated time on clock-out/break and updates Supabase state.
     *
     * @param {"in"|"out"|"break"} action - The clock action to perform.
     */
    const handleClockAction = async (action: 'in' | 'out' | 'break') => {
        const now = new Date().getTime();

        let newAccumulated = accumulatedMs;
        if (clockStatus === 'in' && currentSessionStart) newAccumulated += (now - currentSessionStart);
        const newStart = action === 'in' ? now : null;

        // Optimistic: invalidate immediately so UI feels instant.
        // The realtime event will confirm/refetch shortly after.
        await supabase.from('hr_state').upsert({
            user_id: stableUserId,
            tenant_id: activeTenantId,
            date: today,
            status: action,
            accumulated_ms: newAccumulated,
            session_start: newStart ? new Date(newStart).toISOString() : null
        }, { onConflict: 'tenant_id,user_id,date' });

        await supabase.from('time_logs').insert({
            tenant_id: activeTenantId,
            user_id: stableUserId,
            action: action,
            timestamp: new Date().toISOString(),
            dateString: today
        });

        // Trigger refetch — realtime may take a moment.
        queryClient.invalidateQueries({ queryKey: ['hr', 'state'] });
        queryClient.invalidateQueries({ queryKey: ['hr', 'timeLogs'] });

        const actionLabels = { in: t('hr.clock.clockIn'), out: t('hr.clock.clockOut'), break: t('hr.clock.break'), pause: t('hr.clock.break') };
        logGlobalAction(user, "Stempeluhr", `${t('hr.clock.statusIn')} '${actionLabels[action] || action}'`);
    };

    /**
     * Manually adjusts the total working time for the current day.
     * Useful for correcting missed clock-ins or forgotten logouts.
     *
     * @param {number} deltaMs - Milliseconds to add (positive) or remove (negative).
     * @param {string} comment - Reason for the correction.
     */
    const saveEditedTime = async (deltaMs: number, comment: string) => {
        if (deltaMs === 0) return;

        const newAccumulated = accumulatedMs + deltaMs;

        await supabase.from('hr_state').upsert({
            user_id: stableUserId,
            tenant_id: activeTenantId,
            date: today,
            accumulated_ms: newAccumulated
        }, { onConflict: 'tenant_id,user_id,date' });

        await supabase.from('time_logs').insert({
            tenant_id: activeTenantId,
            user_id: stableUserId,
            action: 'manual_correction',
            deltaMs: deltaMs,
            comment: comment,
            timestamp: new Date().toISOString(),
            dateString: today
        });

        queryClient.invalidateQueries({ queryKey: ['hr', 'state'] });
        queryClient.invalidateQueries({ queryKey: ['hr', 'timeLogs'] });
        logGlobalAction(user, "Zeitkorrektur", `Arbeitszeit korrigiert (${deltaMs > 0 ? '+' : ''}${Math.round(deltaMs / 60000)} Min). Grund: ${comment}`);
    };

    /**
     * Records a manual pause duration, subtracting it from the total working time.
     *
     * @param {number} deltaMs - The duration of the pause in milliseconds.
     * @param {string} comment - Optional comment or reason for the pause.
     */
    const saveManualPause = async (deltaMs: number, comment: string) => {
        if (deltaMs === 0) return;

        const newAccumulated = accumulatedMs - deltaMs;

        await supabase.from('hr_state').upsert({
            user_id: stableUserId,
            tenant_id: activeTenantId,
            date: today,
            accumulated_ms: newAccumulated
        }, { onConflict: 'tenant_id,user_id,date' });

        await supabase.from('time_logs').insert({
            tenant_id: activeTenantId,
            user_id: stableUserId,
            action: 'manual_pause',
            deltaMs: deltaMs,
            comment: comment,
            timestamp: new Date().toISOString(),
            dateString: today
        });

        queryClient.invalidateQueries({ queryKey: ['hr', 'state'] });
        queryClient.invalidateQueries({ queryKey: ['hr', 'timeLogs'] });
        logGlobalAction(user, "Manuelle Pause", `Pause manuell hinzugefügt (${Math.round(deltaMs / 60000)} Min). Grund: ${comment}`);
    };

    /**
     * Submits an absence request (vacation, sick leave, etc.) to Supabase.
     *
     * @param {string} type - The type of request (e.g., 'Urlaub').
     * @param {string} startDate - ISO date string for start.
     * @param {string} endDate - ISO date string for end.
     * @param {boolean} isHalfDay - Whether the request is a half-day.
     */
    const submitRequest = async (type: string, startDate: string, endDate: string, isHalfDay = false) => {
        if (!startDate || !endDate) return;

        const startStr = formatDateDDMMYY(startDate);
        const endStr = formatDateDDMMYY(endDate);

        const userState = user?.holidayState || 'BE';
        const userWorkdays = user?.workdays || [true, true, true, true, true, false, false];
        const currentUserName = user?.name ?? '';
        const days = type === 'Urlaub'
            ? calculateVacationDays(startDate, endDate, userState, isHalfDay, hrSettings, userWorkdays)
            : (isHalfDay ? 0.5 : Math.max(1, Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1));

        const { data: newReq, error } = await supabase.from('hr_requests').insert({
            user_id: stableUserId,
            tenant_id: activeTenantId,
            type: type,
            start_date: startDate,
            end_date: endDate,
            status: 'pending',
            data: {
                isHalfDay,
                userName: currentUserName,
                date: startStr === endStr ? startStr : `${startStr} - ${endStr}`,
                days: days,
            }
        }).select().single();

        if (error) {
            console.error("Error creating absence request:", error);
            return;
        }

        queryClient.invalidateQueries({ queryKey: ['hr', 'requests'] });

        // Fetch the current user's absenceManagers array
        try {
            const { data: userData } = await supabase
                .from('users')
                .select('data')
                .eq('tenant_id', activeTenantId)
                .eq('id', stableUserId)
                .single();

            if (userData) {
                const managers = userData.data?.absenceManagers || [];
                for (const managerName of managers) {
                    await sendPushNotification(getToken, {
                        mentionedName: managerName.trim().toLowerCase(),
                        fromUserName: currentUserName,
                        text: `${currentUserName} hat einen neuen Abwesenheitsantrag (${type}) für ${days} Tage gestellt.`,
                        type: 'hr_absence',
                        traceId: newReq.id
                    });
                }
            }
        } catch (error) {
            console.error("Error creating absence request notifications:", error);
        }

        logGlobalAction(user, "Abwesenheitsantrag", `${type} beantragt (${days} Tage)`);
    };

    // Approved Urlaub/Krankheit days credit the daily target (PaidAbsence 4th
    // arg) so vacation days net 0 in the zeitkonto and hotfix work counts as
    // overtime. Rejected/pending requests are excluded.
    const dailyStats = useMemo(
        () => calculateDailyStats(allTimeLogs, user?.targetHoursPerDay, user?.workdays, (requests || []).filter(r => r.status === 'approved')),
        [allTimeLogs, user?.targetHoursPerDay, user?.workdays, requests]
    );

    return {
        clockStatus,
        accumulatedMs,
        displayMs,
        allTimeLogs: allTimeLogs || [],
        requests,
        dailyStats,
        hrSettings,
        showMissingTimeModal,
        setShowMissingTimeModal,
        missingShiftData,
        setMissingShiftData,
        handleClockAction,
        saveEditedTime,
        saveManualPause,
        submitRequest
    };
}
